import { useState, useEffect, useCallback, useRef } from 'react';
import api from 'services/api';
import { useTenant } from 'context/TenantContext';
import { exportPagePdf } from 'utils/exportPdf';
import { aujourdHui } from '../../../../utils/jour';

export default function GrandLivrePage() {
  // Identite du TENANT pour l'en-tete/pied des exports PDF.
  // Sans raison sociale, NIF et RCCM, l'export est REFUSE (cf. utils/exportPdf.js).
  const tenant = useTenant();
  const [comptes, setComptes] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [compte, setCompte] = useState('');
  const [debut, setDebut] = useState(() => { const d = new Date(); return `${d.getFullYear()}-01-01`; });
  const [fin, setFin] = useState(() => aujourdHui());
  const printRef = useRef();

  useEffect(() => {
    api.get('/syscohada/plan-comptable').then(r => setComptes(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!compte) return;
    setLoading(true);
    try {
      const r = await api.get(`/syscohada/grand-livre?compte=${compte}&debut=${debut}&fin=${fin}`);
      setData(r.data);
    } catch {} finally { setLoading(false); }
  }, [compte, debut, fin]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="gss-toolbar" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select className="gss-input" value={compte} onChange={e => setCompte(e.target.value)} style={{ width: 300 }}>
            <option value="">-- Sélectionner un compte --</option>
            {comptes.map(c => <option key={c.id} value={c.numero}>{c.numero} — {c.libelle}</option>)}
          </select>
          <input type="date" className="gss-input" value={debut} onChange={e => setDebut(e.target.value)} style={{ width: 150 }} />
          <input type="date" className="gss-input" value={fin} onChange={e => setFin(e.target.value)} style={{ width: 150 }} />
        </div>
        {data && <button className="gss-btn gss-btn-primary" onClick={() => exportPagePdf(`Grand livre — ${data.compte.numero} ${data.compte.libelle}`, printRef.current, tenant)}>Exporter PDF</button>}
      </div>

      {!compte && (
        <div className="content-card" style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
          Sélectionnez un compte pour afficher son grand livre
        </div>
      )}

      {loading && <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p>}

      {data && !loading && (
        <>
          <div className="stats-grid stats-grid--3">
            <div className="stat-card">
              <div className="stat-card-header"><span className="stat-card-label">Compte {data.compte.numero}</span></div>
              <div className="stat-card-value" style={{ fontSize: 14 }}>{data.compte.libelle}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-header"><span className="stat-card-label">Solde débiteur</span></div>
              <div className="stat-card-value" style={{ color: '#1565c0' }}>{Number(data.solde_debiteur).toLocaleString()}</div>
              <div className="stat-card-sub">FCFA</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-header"><span className="stat-card-label">Solde créditeur</span></div>
              <div className="stat-card-value" style={{ color: '#2e7d32' }}>{Number(data.solde_crediteur).toLocaleString()}</div>
              <div className="stat-card-sub">FCFA</div>
            </div>
          </div>

          <div className="content-card" ref={printRef}>
            <div className="content-card-title">Grand livre — {data.compte.numero} {data.compte.libelle}</div>
            {data.lignes.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>Aucun mouvement sur ce compte</p>
            ) : (
              <div className="gss-table-wrap">
                <table className="gss-table">
                  <thead><tr><th>Date</th><th>N° pièce</th><th>Libellé</th><th style={{ textAlign: 'right' }}>Débit</th><th style={{ textAlign: 'right' }}>Crédit</th><th style={{ textAlign: 'right' }}>Solde</th></tr></thead>
                  <tbody>
                    {data.lignes.map((l, i) => (
                      <tr key={i}>
                        <td>{l.date_ecriture?.split('T')[0]}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{l.numero_piece}</td>
                        <td style={{ fontWeight: 600 }}>{l.libelle}</td>
                        <td style={{ textAlign: 'right', color: l.debit > 0 ? '#1565c0' : '#ccc' }}>{l.debit > 0 ? Number(l.debit).toLocaleString() : '-'}</td>
                        <td style={{ textAlign: 'right', color: l.credit > 0 ? '#2e7d32' : '#ccc' }}>{l.credit > 0 ? Number(l.credit).toLocaleString() : '-'}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: l.solde_progressif >= 0 ? '#1565c0' : '#DC2626' }}>{Number(l.solde_progressif).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ fontWeight: 700, borderTop: '2px solid #8B1A1A' }}>
                      <td colSpan={3}>SOLDE FINAL</td>
                      <td style={{ textAlign: 'right' }}>{data.solde_debiteur > 0 ? Number(data.solde_debiteur).toLocaleString() : ''}</td>
                      <td style={{ textAlign: 'right' }}>{data.solde_crediteur > 0 ? Number(data.solde_crediteur).toLocaleString() : ''}</td>
                      <td style={{ textAlign: 'right', color: data.solde_final >= 0 ? '#1565c0' : '#DC2626' }}>{Number(data.solde_final).toLocaleString()} F</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
